import {Mapper} from '@s-ui/domain'
import {slugify} from '@s-ui/js/lib/string'

export class TrackingLeadMapper extends Mapper {
  constructor({config}) {
    super({config})
    this._config = config
    this._culture = this._config.get('DEFAULT_CULTURE')

    const {RESERVATION_STATUSES} = config.get('financing')
    const {HIT_INFORMATIONS, LEAD_TYPES} = this._config.get('tracking')
    this._HIT_INFORMATIONS = HIT_INFORMATIONS
    this._LEAD_TYPES = LEAD_TYPES
    this._reservationStatuses = RESERVATION_STATUSES
    this._targetBuyerIds = this._config.get('subscription').TARGET_BUYER_IDS
  }

  setParams = ({products}) => {
    this._products = products

    return this
  }

  getAdHasInstalment = instalmentPrice => (instalmentPrice !== null && instalmentPrice !== undefined ? true : undefined)

  map = ({
    ad,
    adType,
    dealerId,
    filters,
    filtersString,
    financingInstalmentPrice,
    listType,
    subscriptionPriceOption,
    modelDetailLeadType,
    transactionInfo,
    pageType,
    pageNameOrigin
  }) => {
    return {
      ...(ad.make && {brand: ad.make}),
      ...(ad.makeId && {brand_id: ad.makeId}),
      ...(ad.model && {model: ad.model}),
      ...(ad.modelId && {model_id: ad.modelId}),
      ...(ad.fuelType && {fuel_types: ad.fuelType}),
      ...(ad.km && {km: ad.km}),
      ...(ad.price && {price: ad.price}),
      ...(ad.province && {provinces: ad.province}),
      ...(ad.provinceId && {provinces_id: ad.provinceId.toString()}),
      ...(this._products && {products: this._products}),
      seller_type: this._getSellerType({isProfessional: ad.isProfessional}),
      ...(adType && {status_car: this._getStatusCar({adType})}),
      filtered_provinces: this._getFilteredProvinces({filters}),
      ...(financingInstalmentPrice && {has_instalment: this.getAdHasInstalment(financingInstalmentPrice)}),
      ...(transactionInfo?.hasOnlineFinancing && {has_online_financing: transactionInfo.hasOnlineFinancing}),
      ...(transactionInfo?.reservationStatus && {
        has_reservation: transactionInfo?.reservationStatus === this._reservationStatuses?.RESERVABLE
      }),
      ...(financingInstalmentPrice && {instalment_price: financingInstalmentPrice}),
      ...(modelDetailLeadType && this._getModelLeadData({modelDetailLeadType})),
      ...((subscriptionPriceOption || ad.subscriptionPriceOptions) &&
        this._getSubscriptionData({
          targetBuyer: ad.targetBuyer,
          subscriptionPriceOption,
          subscriptionPriceOptions: ad.subscriptionPriceOptions
        })),
      ...(filtersString && {filters: filtersString}),
      ...(this._getPageNameOrigin({pageType, listType, pageNameOrigin}) && {
        page_name_origin: this._getPageNameOrigin({pageType, listType, pageNameOrigin})
      }),
      ...(pageType && {page_type: pageType}),
      has_pricedrop: ad.priceDrop ? 'with_price_drop' : 'without_price_drop',
      ...(typeof ad.offerType?.id === 'number' && {offer_type: this._getOfferType({offerTypeId: ad.offerType.id})}),
      ...(dealerId && {dealer_id: dealerId.toString()})
    }
  }

  _getPageNameOrigin = ({pageType, listType, pageNameOrigin}) => {
    if (pageType === 'detail') return 'detail'
    if (pageNameOrigin) return pageNameOrigin
    if (listType) return listType
  }

  _getModelLeadData = ({modelDetailLeadType}) => {
    const {LEAD_TYPE} = this._config.get('modelDetail')

    return {
      lead_type: this._LEAD_TYPES.MODEL_DETAIL_LEAD,
      hit_information:
        modelDetailLeadType === LEAD_TYPE.TEST_DRIVE
          ? this._HIT_INFORMATIONS.NEWVEHICLE_MODELDETAIL_LEAD_TEST_DRIVE
          : this._HIT_INFORMATIONS.NEWVEHICLE_MODELDETAIL_LEAD_GENERAL_INFO
    }
  }

  _getOfferType = ({offerTypeId}) => {
    const {OFFER_TYPES_TRANSLATIONS} = this._config.get('tracking')
    return slugify(OFFER_TYPES_TRANSLATIONS[offerTypeId]?.[this._culture])
  }

  _getSellerType = ({isProfessional}) => {
    const {SELLER_TYPE_PRIVATE, SELLER_TYPE_PROFESSIONAL} = this._config.get('dealer')
    const {SELLER_TYPES_TRANSLATIONS} = this._config.get('tracking')
    return isProfessional
      ? SELLER_TYPES_TRANSLATIONS[SELLER_TYPE_PROFESSIONAL][this._culture]
      : SELLER_TYPES_TRANSLATIONS[SELLER_TYPE_PRIVATE][this._culture]
  }

  _getStatusCar = ({adType}) => {
    const {offerTypeId, category2Id} = adType
    const {OFFER_TYPES_TRANSLATIONS, CATEGORIES_LEVEL_2_TRANSLATIONS} = this._config.get('tracking')
    const {OFFER_TYPE_SUBSCRIPTION} = this._config.get('search')

    if (offerTypeId === OFFER_TYPE_SUBSCRIPTION) return slugify(OFFER_TYPES_TRANSLATIONS[offerTypeId]?.[this._culture])

    return offerTypeId > 0
      ? OFFER_TYPES_TRANSLATIONS[offerTypeId]?.[this._culture]
      : CATEGORIES_LEVEL_2_TRANSLATIONS[category2Id]?.[this._culture]
  }

  _getFilteredProvinces = ({filters = {}}) => {
    const {provinces} = filters
    if (!provinces) return this._config.get('filteredProvincesResults').UNKNOWN
    return provinces
  }

  _getSubscriptionData = ({targetBuyer, subscriptionPriceOption, subscriptionPriceOptions}) => {
    if (subscriptionPriceOption) {
      return {
        monthly_subscription_fee: `${subscriptionPriceOption.monthlyPrice}`,
        target_buyer: subscriptionPriceOption.targetBuyer?.toLowerCase(),
        allowance_km_included: `${subscriptionPriceOption.kmIncluded}`,
        allowance_period: subscriptionPriceOption.kmIncludedPeriod,
        commitment: `${subscriptionPriceOption.totalMonths}`
      }
    }
    if (subscriptionPriceOptions) {
      const option = subscriptionPriceOptions.reduce((prev, curr) => {
        return prev.monthlyPrice < curr.monthlyPrice ? prev : curr
      })

      return {
        monthly_subscription_fee: `${option?.monthlyPrice}`,
        target_buyer: targetBuyer?.toLowerCase(),
        allowance_km_included: `${option.kmIncluded}`,
        allowance_period: option.kmIncludedPeriod,
        commitment: `${option.totalMonths}`
      }
    }
  }
}
